/* ============================================================
   collection-grid.js  v3 (LIGHT)
   Upload to: Shopify Admin → Themes → Edit Code → Assets
   Features:
   - Lazy image blur-up (IntersectionObserver)
   - Scroll-reveal card entrance (staggered)
   - AJAX Add-to-Cart with loading/success states
   - Wishlist toggle (localStorage)
   - Reduced motion respect
   Removed: Shop Now ripple effect (button removed for FCP/LCP)
============================================================ */

(function () {
  'use strict';

  const PREFERSREDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ──────────────────────────────────────
     1. LAZY IMAGE BLUR-UP
     Uses data-src / data-srcset for
     progressive loading. Tiny placeholder
     src is already inlined (20px wide).
  ────────────────────────────────────── */
  function initLazyImages() {
    const imgs = document.querySelectorAll('.cg-lazy');
    if (!imgs.length) return;

    if (!('IntersectionObserver' in window)) {
      imgs.forEach(img => {
        if (img.dataset.src) img.src = img.dataset.src;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.classList.add('cg-loaded');
      });
      return;
    }

    const imgObs = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const img = entry.target;
        if (img.dataset.src)    img.src    = img.dataset.src;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.onload = () => img.classList.add('cg-loaded');
        img.onerror = () => img.classList.add('cg-loaded');
        obs.unobserve(img);
      });
    }, { rootMargin: '200px 0px' });

    imgs.forEach(img => imgObs.observe(img));
  }

  /* ──────────────────────────────────────
     2. SCROLL-REVEAL CARD ENTRANCE
  ────────────────────────────────────── */
  function initCardReveal() {
    const cards = document.querySelectorAll('.cg-card');
    if (!cards.length) return;

    if (PREFERSREDUCED || !('IntersectionObserver' in window)) {
      cards.forEach(c => c.classList.add('cg-card--visible'));
      return;
    }

    const revObs = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const card = entry.target;
        const delay = (parseInt(card.dataset.revealIdx, 10) || 0) * 80;
        setTimeout(() => card.classList.add('cg-card--visible'), delay);
        obs.unobserve(card);
      });
    }, { threshold: 0.08 });

    cards.forEach((card, i) => {
      card.dataset.revealIdx = i;
      revObs.observe(card);
    });
  }

  /* ──────────────────────────────────────
     3. AJAX ADD TO CART
  ────────────────────────────────────── */
  function initATC() {
    document.querySelectorAll('[data-cg-atc-form]').forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn      = form.querySelector('[data-cg-atc]');
        const variantId = form.querySelector('input[name="id"]').value;
        const qty       = form.querySelector('input[name="quantity"]')?.value || 1;
        if (!btn || !variantId) return;

        const origText = btn.textContent;
        btn.classList.add('is-loading');
        btn.disabled = true;

        try {
          const res = await fetch('/cart/add.js', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id:       parseInt(variantId, 10),
              quantity: parseInt(qty, 10)
            })
          });

          btn.classList.remove('is-loading');

          if (res.ok) {
            btn.classList.add('is-success');
            btn.textContent = '✔ Added';

            document.dispatchEvent(new CustomEvent('cart:refresh'));
            if (window.Shopify && window.Shopify.onCartUpdate) {
              window.Shopify.onCartUpdate();
            }

            setTimeout(() => {
              btn.classList.remove('is-success');
              btn.textContent = origText;
              btn.disabled    = false;
            }, 2200);
          } else {
            const err = await res.json().catch(() => ({}));
            btn.textContent = err.description || 'Error — try again';
            btn.disabled    = false;
            setTimeout(() => { btn.textContent = origText; }, 2800);
          }
        } catch (_) {
          btn.classList.remove('is-loading');
          btn.textContent = 'Retry';
          btn.disabled    = false;
          setTimeout(() => { btn.textContent = origText; }, 2500);
        }
      });
    });
  }

  /* ──────────────────────────────────────
     4. WISHLIST TOGGLE
  ────────────────────────────────────── */
  function initWishlist() {
    const STORAGE_KEY = 'cg_wishlist';

    function getList() {
      try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
      catch (_) { return []; }
    }
    function saveList(list) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); }
      catch (_) {}
    }

    const saved = getList();
    document.querySelectorAll('[data-cg-wish]').forEach(btn => {
      const id = btn.dataset.cgWish;
      if (saved.includes(id)) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
      }

      btn.addEventListener('click', () => {
        const list    = getList();
        const idx     = list.indexOf(id);
        const isActive = btn.classList.contains('is-active');

        if (isActive) {
          list.splice(idx, 1);
          btn.classList.remove('is-active');
          btn.setAttribute('aria-pressed', 'false');
        } else {
          list.push(id);
          btn.classList.add('is-active');
          btn.setAttribute('aria-pressed', 'true');
        }
        saveList(list);

        if (!PREFERSREDUCED) {
          btn.style.transform = 'scale(1.3)';
          setTimeout(() => { btn.style.transform = ''; }, 220);
        }
      });
    });
  }

  /* ──────────────────────────────────────
     5. JUDGE.ME BADGE SAFETY REFRESH
     Some themes/apps miss badges that are
     already in the DOM if Judge.me's own
     script ran before this section painted.
     This forces a re-scan — harmless if
     Judge.me isn't installed.
  ────────────────────────────────────── */
  function refreshJudgeMeBadges() {
    if (window.jdgm && typeof window.jdgm.badge?.updateBadges === 'function') {
      try { window.jdgm.badge.updateBadges(); } catch (_) {}
    }
  }

  /* ──────────────────────────────────────
     INIT
  ────────────────────────────────────── */
  function init() {
    initLazyImages();
    initCardReveal();
    initATC();
    initWishlist();
    refreshJudgeMeBadges();
    // retry once more after a short delay in case Judge.me's script
    // loads slightly after ours
    setTimeout(refreshJudgeMeBadges, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();